import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  ChevronDown,
  Eraser,
  EyeOff,
  FileText, Lock,
  Maximize2,
  MessageSquare,
  Mic, MicOff, Minus,
  Monitor, MonitorOff,
  Pencil, Play,
  Send, Sparkles, Terminal, Trash2,
  Users, Video, VideoOff, X
} from 'lucide-react';
import Peer from 'peerjs';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import io from 'socket.io-client';
import AtelierDetail from "../components/AtelierDetail";

import { useAuth } from "../contexts/AuthContext";
import { ATELIERS_LIBRARY, useAtelierUnlock } from '../hooks/useAtelierUnlock';

const CANVAS_BG   = '#0a0a14';

// ── DESIGN TOKENS ──────────────────────────────────────────────────────────
const C = {
  bg:        '#020617',
  surface:   'rgba(15,23,42,0.7)',
  surfaceHi: 'rgba(15,23,42,0.95)',
  border:    'rgba(255,255,255,0.06)',
  borderCy:  'rgba(34,211,238,0.2)',
  text:      '#f1f5f9',
  textSub:   '#94a3b8',
  textFaint: '#475569',
  textMute:  'rgba(255,255,255,0.38)',
  cyan:      '#22d3ee',
  amber:     '#f59e0b',
  emerald:   '#10b981',
  rose:      '#fb7185',
  orange:    '#f97316',
};

const Mono = ({ children, size = 11, color = C.textMute, w = false, style = {} }) => (
  <span
    style={{
      fontFamily: "'DM Mono', 'Roboto Mono', monospace",
      fontSize: size,
      letterSpacing: '0.06em',
      color,
      fontWeight: w ? 700 : 400,
      lineHeight: 1,
      ...style,
    }}
  >
    {children}
  </span>
);

// ── GLOBAL CSS ─────────────────────────────────────────────────────────────
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=Roboto+Mono:wght@400;500;700&display=swap');
  @keyframes pulse{0%,100%{opacity:.4;transform:scale(1)}50%{opacity:1;transform:scale(1.3)}}
  *{box-sizing:border-box}
  ::-webkit-scrollbar{width:3px} ::-webkit-scrollbar-track{background:transparent} ::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.06)}

  /* ── RESPONSIVE ── */

  /* Ateliers list page */
  .ateliers-content { padding: 16px; }
  .ateliers-grid { display: grid; grid-template-columns: 1fr; gap: 12px; }
  .filters-row { display: flex; gap: 5px; flex-wrap: wrap; margin-bottom: 20px; }

  /* Detail view */
  .detail-content { padding: 14px; }
  .detail-hero-grid { display: flex; flex-direction: column; gap: 20px; }
  .detail-stats-row { display: flex; gap: 8px; flex-wrap: wrap; }
  .theory-grid { display: flex; flex-direction: column; gap: 12px; height: auto; }
  .theory-sidebar { border: 1px solid rgba(255,255,255,0.06); background: rgba(15,23,42,0.7); overflow: hidden; }
  .slide-list { display: flex; gap: 6px; flex-wrap: wrap; padding: 8px; }
  .slide-list-desktop { display: none; }
  .sections-nav { display: flex; gap: 0; border-bottom: 1px solid rgba(255,255,255,0.06); margin-bottom: 18px; overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: none; }
  .sections-nav::-webkit-scrollbar { display: none; }

  /* Video room */
  .video-room-main { flex-direction: column; overflow-y: auto; -webkit-overflow-scrolling: touch; }
  .video-grid { display: grid; gap: 6px; padding: 8px; grid-template-columns: 1fr; grid-auto-rows: auto; }
  .video-tile { min-height: 140px; max-height: 200px; }
  .video-sidebar { width: 100%; border-left: none; border-top: 1px solid rgba(255,255,255,0.06); margin: 0; border-radius: 0; flex-shrink: 0; height: 360px; }
  .video-controls { bottom: 380px; }
  .video-room-header { padding: 0 12px; }
  .video-room-title { font-size: 12px; }

  @media (min-width: 480px) {
    .ateliers-grid { grid-template-columns: 1fr; }
  }

  @media (min-width: 640px) {
    .ateliers-content { padding: 20px 24px; }
    .ateliers-grid { grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 14px; }
    .detail-content { padding: 20px 24px; }
    .detail-hero-grid { flex-direction: row; align-items: center; justify-content: space-between; }
  }

  @media (min-width: 768px) {
    .ateliers-content { padding: 24px 32px; }
    .theory-grid { flex-direction: row; height: 400px; }
    .theory-sidebar { width: 200px; flex-shrink: 0; }
    .slide-list { display: none; }
    .slide-list-desktop { display: block; padding: 8px; }
    .sections-nav { overflow-x: visible; }
    .video-room-main { flex-direction: row; overflow-y: hidden; }
    .video-grid { grid-template-columns: 1fr 1fr; grid-auto-rows: 1fr; padding: 12px; }
    .video-tile { min-height: 160px; max-height: none; }
    .video-sidebar { width: 300px; height: auto; border-left: 1px solid rgba(255,255,255,0.06); border-top: none; margin: 8px 8px 8px 0; border-radius: 8px; }
    .video-controls { bottom: 70px; }
  }

  @media (min-width: 1024px) {
    .video-grid { grid-template-columns: repeat(3, 1fr); }
  }
`;

// ── PRIMITIVES ─────────────────────────────────────────────────────────────
const PerspectiveGrid = () => (
  <div style={{
    position:'fixed', inset:0, zIndex:0, pointerEvents:'none',
    backgroundImage:`linear-gradient(to right, rgba(34,211,238,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(245,158,11,0.05) 1px, transparent 1px)`,
    backgroundSize:'120px 120px',
    maskImage:'radial-gradient(circle at 50% 50%, black 60%, transparent 100%)',
    WebkitMaskImage:'radial-gradient(circle at 50% 50%, black 60%, transparent 100%)',
    opacity:0.5,
  }} />
);

const Corners = ({ colorTL=C.cyan, colorBR=C.amber, size=7 }) => (
  <>
    <span style={{ position:'absolute', top:-1, left:-1, width:size, height:size, borderTop:`1.5px solid ${colorTL}`, borderLeft:`1.5px solid ${colorTL}` }} />
    <span style={{ position:'absolute', bottom:-1, right:-1, width:size, height:size, borderBottom:`1.5px solid ${colorBR}`, borderRight:`1.5px solid ${colorBR}` }} />
  </>
);

const M = ({ children, size=11, color=C.textFaint, w=false, style={} }) => (
  <span style={{ fontFamily:"'Roboto Mono', monospace", fontSize:size, letterSpacing:'0.03em', color, fontWeight:w?700:400, lineHeight:1, ...style }}>
    {children}
  </span>
);

const Dot = ({ color=C.cyan, pulse=false, size=5 }) => (
  <span style={{ display:'inline-block', width:size, height:size, borderRadius:'50%', background:color, boxShadow:`0 0 7px ${color}`, flexShrink:0, animation:pulse?'pulse 2s ease-in-out infinite':'none' }} />
);

const Tag = ({ children, color }) => (
  <span style={{
    padding:'2px 7px', fontSize:9, fontFamily:"'Roboto Mono', monospace",
    letterSpacing:'0.08em', textTransform:'uppercase',
    background:`${color}10`, border:`1px solid ${color}25`, color,
  }}>{children}</span>
);

function useIsMobile(bp = 768) {
  const [v, setV] = useState(() => window.innerWidth < bp);
  useEffect(() => {
    const h = () => setV(window.innerWidth < bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return v;
}

// ── CONTEXTE SESSION ────────────────────────────────────────────────────────
const VideoSessionContext = createContext(null);
export const useVideoSession = () => useContext(VideoSessionContext);



const Whiteboard = () => {
  const { socket, roomId, isHost } = useVideoSession();

  const canvasRef  = useRef(null);
  const isDrawing  = useRef(false);
  const lastPos    = useRef(null);
  const ctxRef     = useRef(null);
  const [tool,  setTool]  = useState('pen');
  const [color, setColor] = useState('#22d3ee');
  const [size,  setSize]  = useState(3);
  const COLORS = ['#22d3ee','#f97316','#10b981','#a855f7','#ef4444','#ffffff','#64748b'];

  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const dpr  = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width  = rect.width  * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctxRef.current = ctx;
    ctx.fillStyle = CANVAS_BG;
    ctx.fillRect(0, 0, canvas.width / dpr, canvas.height / dpr);
  }, []);

  useEffect(() => {
    if (!socket) return;
    const handleDraw  = ({ x0,y0,x1,y1,color:c,size:s,tool:t }) => drawLine(ctxRef.current,x0,y0,x1,y1,c,s,t);
    const handleClear = () => {
      const canvas = canvasRef.current; const ctx = ctxRef.current; if (!canvas||!ctx) return;
      ctx.fillStyle = CANVAS_BG;
      ctx.fillRect(0, 0, canvas.width/(window.devicePixelRatio||1), canvas.height/(window.devicePixelRatio||1));
    };
    const handleState = ({ imageData }) => {
      if (!imageData||!ctxRef.current||!canvasRef.current) return;
      const img = new Image();
      img.onload = () => ctxRef.current.drawImage(img, 0, 0);
      img.src = imageData;
    };
    socket.on('whiteboard-draw',  handleDraw);
    socket.on('whiteboard-clear', handleClear);
    socket.on('whiteboard-state', handleState);
    socket.emit('whiteboard-request-state', { roomId });
    return () => {
      socket.off('whiteboard-draw',  handleDraw);
      socket.off('whiteboard-clear', handleClear);
      socket.off('whiteboard-state', handleState);
    };
  }, [socket, roomId]);

  const drawLine = (ctx,x0,y0,x1,y1,col,sz,tl) => {
    if (!ctx) return;
    ctx.save();
    ctx.globalCompositeOperation = 'source-over';
    ctx.strokeStyle = tl === 'eraser' ? CANVAS_BG : col;
    ctx.lineWidth   = tl === 'eraser' ? sz * 4 : sz;
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.beginPath(); ctx.moveTo(x0,y0); ctx.lineTo(x1,y1); ctx.stroke();
    ctx.restore();
  };

  const getPos = e => {
    const canvas = canvasRef.current; const rect = canvas.getBoundingClientRect(); const dpr = window.devicePixelRatio||1;
    const cx = e.touches?.[0]?.clientX ?? e.clientX;
    const cy = e.touches?.[0]?.clientY ?? e.clientY;
    return { x:(cx-rect.left)*(canvas.width/rect.width)/dpr, y:(cy-rect.top)*(canvas.height/rect.height)/dpr };
  };

  const onDown = e => { e.preventDefault(); isDrawing.current=true; lastPos.current=getPos(e); };
  const onMove = e => {
    if (!isDrawing.current) return;
    e.preventDefault();
    const pos = getPos(e);
    drawLine(ctxRef.current, lastPos.current.x, lastPos.current.y, pos.x, pos.y, color, size, tool);
    // FIX: auto-save sur chaque trait pour les nouveaux arrivants
    socket?.emit('whiteboard-draw', { roomId, x0:lastPos.current.x, y0:lastPos.current.y, x1:pos.x, y1:pos.y, color, size, tool });
    lastPos.current = pos;
  };
  const onUp = () => {
    isDrawing.current = false;
    // Auto-save canvas state pour les futurs arrivants
    const canvas = canvasRef.current;
    if (canvas && socket) {
      const imageData = canvas.toDataURL();
      socket.emit('whiteboard-save-state', { roomId, imageData });
    }
  };

  const handleClear = () => {
    if (!isHost) return; // RÔLE : seul l'hôte peut effacer
    const canvas = canvasRef.current; const ctx = ctxRef.current; if (!canvas||!ctx) return;
    ctx.fillStyle = CANVAS_BG;
    ctx.fillRect(0, 0, canvas.width/(window.devicePixelRatio||1), canvas.height/(window.devicePixelRatio||1));
    socket?.emit('whiteboard-clear', { roomId });
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>
      <div style={{ flexShrink:0, display:'flex', alignItems:'center', gap:6, padding:'8px 10px', borderBottom:`1px solid ${C.border}`, background:'rgba(0,0,0,0.3)', flexWrap:'wrap' }}>
        <div style={{ display:'flex', gap:3 }}>
          {[{id:'pen',icon:Pencil},{id:'eraser',icon:Eraser},{id:'line',icon:Minus}].map(({id,icon:Icon})=>(
            <button key={id} onClick={()=>setTool(id)}
              style={{ padding:5, background:tool===id?'rgba(34,211,238,0.15)':'none', border:`1px solid ${tool===id?'rgba(34,211,238,0.3)':'transparent'}`, cursor:'pointer', color:tool===id?C.cyan:C.textFaint, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <Icon size={12}/>
            </button>
          ))}
        </div>
        <div style={{ width:1, height:14, background:C.border }}/>
        <div style={{ display:'flex', gap:4 }}>
          {COLORS.map(c=>(
            <button key={c} onClick={()=>setColor(c)}
              style={{ width:12, height:12, borderRadius:'50%', background:c, border:color===c?'2px solid white':'1px solid transparent', cursor:'pointer', opacity:color===c?1:0.6, flexShrink:0 }}/>
          ))}
        </div>
        <input type="range" min="1" max="20" value={size} onChange={e=>setSize(Number(e.target.value))}
          style={{ width:50, accentColor:C.cyan }}/>
        {/* FIX: bouton CLR visible seulement pour l'hôte */}
        {isHost && (
          <button onClick={handleClear}
            style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:4, padding:'3px 8px', background:'none', border:`1px solid rgba(251,113,133,0.2)`, color:'rgba(251,113,133,0.6)', cursor:'pointer', fontSize:9, fontFamily:"'Roboto Mono', monospace", letterSpacing:'0.1em' }}>
            <Trash2 size={9}/> CLR
          </button>
        )}
      </div>
      <div style={{ flex:1, position:'relative', overflow:'hidden' }}>
        <canvas ref={canvasRef}
          style={{ position:'absolute', inset:0, width:'100%', height:'100%', cursor:tool==='eraser'?'cell':'crosshair', touchAction:'none' }}
          onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
          onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp}/>
      </div>
    </div>
  );
};

const SharedNotes = () => {
  const { socket, roomId } = useVideoSession();
  const [notes, setNotes] = useState('');
  const debRef = useRef(null);

  useEffect(() => {
    if (!socket) return;
    const h = ({ content }) => setNotes(content);
    socket.on('notes-update', h);
    socket.emit('notes-request', { roomId });
    return () => socket.off('notes-update', h);
  }, [socket, roomId]);

  const onChange = e => {
    setNotes(e.target.value);
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => {
      socket?.emit('notes-update', { roomId, content:e.target.value, editor:'Vous' });
    }, 300);
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>
      <div style={{ padding:'8px 12px', borderBottom:`1px solid ${C.border}`, background:'rgba(0,0,0,0.3)', display:'flex', alignItems:'center', gap:8 }}>
        <Dot color={C.emerald} pulse size={4}/>
        <M size={9} color={C.textFaint}>Notes_Collaboratives · Temps_Réel</M>
      </div>
      <textarea value={notes} onChange={onChange}
        placeholder="Prenez des notes partagées avec votre intervenant..."
        style={{ flex:1, background:'transparent', border:'none', outline:'none', padding:'12px', color:C.textSub, fontFamily:"'Roboto Mono', monospace", fontSize:11, lineHeight:1.7, resize:'none' }}/>
    </div>
  );
};

const VideoTile = ({ stream, name, isLocal = false, isMuted = false, isVideoOff = false, isHidden = false, accent = C.cyan, isMobile = false }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (!videoRef.current || !stream) return;
    videoRef.current.srcObject = stream;
    // Sur iOS, autoplay nécessite play() explicite
    videoRef.current.play().catch(() => {});
  }, [stream]);

  return (
    <div style={{
      position:'relative',
      background:'#080818',
      border:`1px solid ${isLocal ? C.borderCy : C.border}`,
      overflow:'hidden',
      minHeight: isMobile ? 130 : 150,
      borderRadius:4,
      transition:'border-color 0.2s',
    }}>
      {/* Vidéo ou placeholder */}
      {(isHidden || isVideoOff || !stream) ? (
        <div style={{ width:'100%', height:'100%', minHeight: isMobile ? 130 : 150, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:8, background:'rgba(8,8,24,0.95)' }}>
          <div style={{ width:40, height:40, borderRadius:'50%', background:`${accent}15`, border:`1px solid ${accent}30`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, fontWeight:700, color:accent }}>
            {name?.[0]?.toUpperCase() || '?'}
          </div>
          <M size={9} color={C.textFaint}>{isHidden ? 'Cam_Masquée' : isVideoOff ? 'Cam_Off' : 'En_attente'}</M>
        </div>
      ) : (
        <video
          ref={videoRef}
          autoPlay
          muted={isLocal}
          playsInline
          style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}
        />
      )}

      {/* Badge nom */}
      <div style={{ position:'absolute', bottom:6, left:6, display:'flex', gap:4, alignItems:'center' }}>
        <div style={{ padding:'2px 7px', background: isLocal ? C.cyan : 'rgba(0,0,0,0.75)', fontFamily:"'Roboto Mono', monospace", fontSize:8, fontWeight:700, color: isLocal ? '#000' : C.text, letterSpacing:'0.08em' }}>
          {name}
        </div>
        {isMuted && (
          <div style={{ padding:'2px 5px', background:'rgba(251,113,133,0.85)', display:'flex', alignItems:'center' }}>
            <MicOff size={8} color="#fff"/>
          </div>
        )}
      </div>

      {/* Indicateur hôte */}
      {isLocal && (
        <div style={{ position:'absolute', top:6, right:6, padding:'2px 6px', background:`${C.amber}20`, border:`1px solid ${C.amber}40`, fontFamily:"'Roboto Mono', monospace", fontSize:7, color:C.amber, letterSpacing:'0.1em' }}>
          VOUS
        </div>
      )}
    </div>
  );
};

// ── SALLE VIDÉO ────────────────────────────────────────────────────────────
const AtelierVideoRoom = ({ atelier, onClose, onMinimize, isHost = false, userName: userNameProp }) => {
  const roomId   = `atelier-${atelier.slug}`;
  const isMobile = useIsMobile(768);

  // FIX: fallback uniquement si prop absente
  const userName = useMemo(() => {
    if (userNameProp) return userNameProp;
    const k = 'pesa_username';
    let n = sessionStorage.getItem(k);
    if (!n) {
      n = `User_${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
      sessionStorage.setItem(k, n);
    }
    return n;
  }, [userNameProp]);

  // ── State ──────────────────────────────────────────────────────────────────
  const [isMuted,       setIsMuted]       = useState(false);
  const [isVideoOff,    setVideoOff]      = useState(false);
  const [isLocalHidden, setLocalHidden]   = useState(false); // FIX: masquer sa propre vignette
  const [activeTab,     setActiveTab]     = useState('whiteboard');
  const [messages,      setMessages]      = useState([]);
  const [msgInput,      setMsgInput]      = useState('');
  const [remotes,       setRemotes]       = useState({});       // { [peerId]: { stream, name, role } }
  const [screenStream,  setScreenStream]  = useState(null);
  const [sidebarOpen,   setSidebarOpen]   = useState(!isMobile);
  const [socketInstance, setSocketInstance] = useState(null);
  const [localStreamReady, setLocalStreamReady] = useState(null);

  // ── Refs ───────────────────────────────────────────────────────────────────
  const localVideoRef  = useRef(null);
  const socketRef      = useRef(null);
  const peerRef        = useRef(null);
  const localStream    = useRef(null);   // FIX: c'est lui qui compte (localStreamRef était mort)
  const outgoingCalls  = useRef(new Map()); // pid → call
  const msgEndRef      = useRef(null);
  const streamReadyRef = useRef(null);         // Promise
  const streamResolveRef = useRef(null);

  // ── Scroll chat ────────────────────────────────────────────────────────────
  useEffect(() => {
    msgEndRef.current?.scrollIntoView({ behavior:'smooth' });
  }, [messages]);

  // ── Acquisition stream local ────────────────────────────────────────────────
  useEffect(() => {
    // Créer la promise AVANT getUserMedia
    streamReadyRef.current = new Promise(resolve => {
      streamResolveRef.current = resolve;
    });

    navigator.mediaDevices
      .getUserMedia({ video: true, audio: true })
      .then(stream => {
        localStream.current = stream;
        if (localVideoRef.current) localVideoRef.current.srcObject = stream;
        streamResolveRef.current(stream);
        setLocalStreamReady(stream);
      })
      .catch(err => {
        console.error('[LOCAL] getUserMedia:', err);
        streamResolveRef.current(null);     // évite un blocage infini
      });
      
      

    return () => {
      localStream.current?.getTracks().forEach(t => t.stop());
    };
  }, []);

  // ── addRemote : FIX dédoublonnage — remplace toujours, n'accumule pas ─────
  const addRemote = useCallback((pid, stream, name, role) => {
    setRemotes(prev => ({
      ...prev,
      [pid]: {
        stream,
        name: name || `User_${pid.slice(-4).toUpperCase()}`,
        role: role || 'guest',
      },
    }));
  }, []);

  // ── Socket.IO + PeerJS ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!roomId) return;

    const isProd = process.env.NODE_ENV === 'production';
    const protocol = window.location.protocol;   
    const hostname = window.location.hostname; 
    const socketUrl = isProd 
      ? 'https://api.pesacore.fr/video'
      : `${protocol}//${hostname}:5000/video`;

    const socket = io(socketUrl, {
      withCredentials: true,
      transports: ['polling'],           
      reconnection: true,
      reconnectionAttempts: 15,
      reconnectionDelay: 1000,
      rejectUnauthorized: false,         
      forceNew: true,
      // autoConnect: true,
    });

    socketRef.current = socket;
    setSocketInstance(socket);

    // ====================== PEERJS ======================
    const peer = new Peer(undefined, {
      host: isProd ? 'api.pesacore.fr' : window.location.hostname,
      port: isProd ? 443 : 5000,
      path: '/peerjs',
      secure: protocol === 'https:',           
      debug: isProd ? 1 : 3,
      
    });

    peerRef.current = peer;

    const callPeer = async (peerId, peerName, peerRole) => {
      const stream = await streamReadyRef.current;   // attend le stream
      if (!stream) return;
      const call = peer.call(peerId, stream, {
        metadata: { userName, role: isHost ? 'host' : 'guest' }
      });
      outgoingCalls.current.set(peerId, call);
      call.on('stream', remoteStream => addRemote(peerId, remoteStream, peerName, peerRole));
      call.on('close', () => setRemotes(p => { const n = {...p}; delete n[peerId]; return n; }));
    };

    peer.on('call', async (call) => {
      const stream = await streamReadyRef.current;   // attend le stream
      if (!stream) return;
      call.answer(stream);
      const { userName: n, role } = call.metadata || {};
      call.on('stream', remoteStream => addRemote(call.peer, remoteStream, n, role));
      call.on('close', () => setRemotes(p => { const n2 = {...p}; delete n2[call.peer]; return n2; }));
    });

    // Appeler les pairs déjà présents
    socket.on('existing-peers', (peers) => {
      peers.forEach(({ peerId, userName: n, role }) => callPeer(peerId, n, role));
    });

    // Un nouveau pair arrive → il nous appelle (ou on l'appelle)
    socket.on('user-connected', ({ peerId, userName: n, role }) => {
      callPeer(peerId, n, role);
    });

    // Répondre aux appels entrants
    peer.on('call', (call) => {
      if (!localStream.current) return;
      call.answer(localStream.current);
      const { userName: n, role } = call.metadata || {};
      call.on('stream', remoteStream => addRemote(call.peer, remoteStream, n, role));
      call.on('close', () => setRemotes(p => { const n2 = {...p}; delete n2[call.peer]; return n2; }));
    });

    socket.on('user-disconnected', (peerId) => {
      outgoingCalls.current.get(peerId)?.close();
      outgoingCalls.current.delete(peerId);
      setRemotes(p => { const n = {...p}; delete n[peerId]; return n; });
    });

    socket.on('newMessage', (msg) => setMessages(p => [...p, msg]));

    return () => { socket.disconnect(); peer.destroy(); };
  }, [roomId, userName, isHost, addRemote]);

  // ── Contrôles médias ───────────────────────────────────────────────────────
  const toggleMute = useCallback(() => {
    localStream.current?.getAudioTracks().forEach(t => (t.enabled = !t.enabled));
    setIsMuted(v => !v);
  }, []);

  const toggleVideo = useCallback(() => {
    localStream.current?.getVideoTracks().forEach(t => (t.enabled = !t.enabled));
    setVideoOff(v => !v);
  }, []);

  // FIX: toggleScreenShare utilisait localStreamRef (jamais assigné) → localStream.current
  const toggleScreenShare = useCallback(async () => {
    if (screenStream) {
      screenStream.getTracks().forEach(t => t.stop());
      setScreenStream(null);
      // Remettre la webcam dans tous les peer calls actifs
      const videoTrack = localStream.current?.getVideoTracks()[0];
      if (videoTrack && peerRef.current?._connections) {
        Object.values(peerRef.current._connections).flat().forEach(conn => {
          const sender = conn.peerConnection?.getSenders().find(s => s.track?.kind === 'video');
          if (sender) sender.replaceTrack(videoTrack);
        });
      }
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video:true });
      setScreenStream(stream);
      const screenTrack = stream.getVideoTracks()[0];
      if (peerRef.current?._connections) {
        Object.values(peerRef.current._connections).flat().forEach(conn => {
          const sender = conn.peerConnection?.getSenders().find(s => s.track?.kind === 'video');
          if (sender) sender.replaceTrack(screenTrack);
        });
      }
      screenTrack.onended = () => toggleScreenShare();
    } catch (err) {
      console.error('[SCREEN] Erreur:', err);
    }
  }, [screenStream]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }, []);

  // ── Chat send ──────────────────────────────────────────────────────────────
  const sendMsg = useCallback(() => {
    if (!msgInput.trim() || !socketRef.current) return;
    const msg = {
      sender: userName,
      text:   msgInput,
      time:   new Date().toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' }),
      self:   true,
    };
    socketRef.current.emit('sendMessage', { roomId, ...msg, self:false });
    setMessages(p => [...p, msg]);
    setMsgInput('');
  }, [msgInput, userName, roomId]);

  // ── Contexte stable (FIX: useMemo pour éviter re-renders en cascade) ───────
  const sessionCtx = useMemo(() => ({
    socket:   socketInstance,
    roomId,
    userName,
    remotes,
    isHost,
    accent:   atelier.accentColor || C.cyan,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [socketInstance, roomId, userName, remotes, isHost, atelier.accentColor]);

  // ── Layout ─────────────────────────────────────────────────────────────────
  const totalP    = Object.keys(remotes).length + 1;
  const accent    = atelier.accentColor || C.cyan;
  const remoteArr = Object.entries(remotes);

  const TABS = [
    { id:'whiteboard',   icon:Pencil,        label:'Board' },
    { id:'chat',         icon:MessageSquare, label:'Chat'  },
    { id:'notes',        icon:FileText,      label:'Notes' },
    { id:'participants', icon:Users,         label:'Nodes' },
  ];

  // Grid columns selon nb participants
  const videoCols = isMobile
    ? '1fr'
    : totalP === 1 ? '1fr'
    : totalP === 2 ? '1fr 1fr'
    : 'repeat(3,1fr)';

  // ── CSS spécifique à ce composant ──────────────────────────────────────────
  const roomCSS = `
    @keyframes pulse{0%,100%{opacity:.4;transform:scale(1)}50%{opacity:1;transform:scale(1.3)}}
    .vroom-ctrl-btn {
      display:flex; align-items:center; justify-content:center;
      width:38px; height:38px; border:none; border-radius:2px;
      cursor:pointer; transition:all 0.15s; color:#fff;
      background:rgba(255,255,255,0.06);
    }
    .vroom-ctrl-btn:hover { background:rgba(255,255,255,0.12); }
    .vroom-ctrl-btn.active { background:${C.rose}; }
    .vroom-ctrl-btn.screen-active { background:rgba(34,211,238,0.2); color:${C.cyan}; }
    .vroom-pip-btn {
      display:flex; align-items:center; gap:6px; padding:5px 12px;
      background:rgba(255,255,255,0.04); border:1px solid ${C.border};
      color:${C.textSub}; cursor:pointer; font-family:'Roboto Mono',monospace;
      font-size:9px; letter-spacing:0.1em; text-transform:uppercase;
      transition:all 0.15s;
    }
    .vroom-pip-btn:hover { background:rgba(255,255,255,0.08); color:${C.text}; }    
  `;

  // ══════════════════════════════════════════════════════════════════════════
  return (
    <VideoSessionContext.Provider value={sessionCtx}>
      <style>{roomCSS}</style>

      <motion.div
        initial={{ opacity:0 }}
        animate={{ opacity:1 }}
        exit={{ opacity:0 }}
        style={{
          position:'fixed', inset:0, zIndex:2000,
          background:C.bg,
          display:'flex', flexDirection:'column',
          fontFamily:"'Plus Jakarta Sans', sans-serif",
        }}
      >
        {/* ── HEADER ─────────────────────────────────────────────────────── */}
        <header style={{
          height:48, flexShrink:0,
          background:'rgba(2,6,23,0.97)',
          borderBottom:`1px solid ${C.border}`,
          display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'0 12px', gap:8,
        }}>
          {/* Titre + statut */}
          <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
            <div style={{ position:'relative', width:28, height:28, flexShrink:0, border:`1px solid rgba(34,211,238,0.2)`, background:'rgba(34,211,238,0.04)', display:'flex', alignItems:'center', justifyContent:'center', color:C.cyan }}>
              <Corners size={4}/>
              <Terminal size={12}/>
            </div>
            <div style={{ minWidth:0 }}>
              <M size={isMobile ? 9 : 10} color={C.text} w style={{ display:'block', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth: isMobile ? '160px' : '320px' }}>
                {atelier.title}
              </M>
              <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:1 }}>
                <Dot color={C.emerald} pulse size={4}/>
                <M size={8} color={C.textFaint}>
                  Live · {totalP}p · {isHost ? '⚙ Hôte' : 'Participant'} · {userName}
                </M>
              </div>
            </div>
          </div>

          {/* Actions header */}
          <div style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
            {/* FIX: bouton Réduire → PiP sans perdre la connexion */}
            {onMinimize && (
              <button className="vroom-pip-btn" onClick={onMinimize}>
                <ChevronDown size={11}/>
                {!isMobile && 'Réduire'}
              </button>
            )}
            <button onClick={onClose}
              style={{ display:'flex', alignItems:'center', gap:5, padding:'5px 10px', background:'rgba(251,113,133,0.05)', border:`1px solid rgba(251,113,133,0.2)`, color:C.rose, cursor:'pointer', fontFamily:"'Roboto Mono', monospace", fontSize:9, letterSpacing:'0.1em', textTransform:'uppercase', transition:'all 0.15s' }}
              onMouseEnter={e=>e.currentTarget.style.background='rgba(251,113,133,0.12)'}
              onMouseLeave={e=>e.currentTarget.style.background='rgba(251,113,133,0.05)'}>
              <X size={11}/>{!isMobile && ' Quitter'}
            </button>
          </div>
        </header>

        {/* ── MAIN ───────────────────────────────────────────────────────── */}
        <main style={{
          flex:1, display:'flex',
          flexDirection: isMobile ? 'column' : 'row',
          overflow:'hidden', position:'relative',
        }}>

          {/* Zone vidéo */}
          <div style={{
            flex:1, display:'flex', flexDirection:'column',
            overflow:'hidden', position:'relative',
          }}>
            {/* Grid vidéos */}
            <div style={{
              flex:1,
              display:'grid',
              gap:6,
              padding: isMobile ? '8px' : '12px',
              gridTemplateColumns: videoCols,
              gridAutoRows: isMobile ? 'auto' : '1fr',
              alignContent:'start',
              overflowY: isMobile ? 'auto' : 'hidden',
            }}>
              {/* Vignette locale — FIX: toggle isLocalHidden */}
              {!isLocalHidden && (
                <VideoTile
                  stream={localStreamReady}
                  name={userName}
                  isLocal
                  isMuted={isMuted}
                  isVideoOff={isVideoOff}
                  accent={accent}
                  isMobile={isMobile}
                />
              )}

              {/* Vignettes distantes — FIX: clé stable = pid */}
              {remoteArr.map(([pid, p]) => (
                <VideoTile
                  key={pid}
                  stream={p.stream}
                  name={p.name}
                  accent={p.role === 'host' ? C.amber : C.cyan}
                  isMobile={isMobile}
                />
              ))}

              {/* Placeholder attente */}
              {remoteArr.length === 0 && (
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', opacity:0.2, gap:8, padding:20, minHeight: isMobile ? 100 : 140 }}>
                  <Users size={28} color={C.cyan}/>
                  <M size={9} color={C.cyan}>En_attente_intervenant...</M>
                </div>
              )}
            </div>

            {/* ── CONTRÔLES FLOTTANTS ───────────────────────────────────── */}
            <div style={{
              flexShrink:0,
              display:'flex', justifyContent:'center', alignItems:'center',
              gap:6,
              padding:'10px 12px',
              background:'rgba(2,6,23,0.92)',
              borderTop:`1px solid ${C.border}`,
            }}>
              {/* Micro */}
              <button className={`vroom-ctrl-btn${isMuted ? ' active' : ''}`} onClick={toggleMute} title={isMuted ? 'Activer micro' : 'Couper micro'}>
                {isMuted ? <MicOff size={15}/> : <Mic size={15}/>}
              </button>

              {/* Caméra */}
              <button className={`vroom-ctrl-btn${isVideoOff ? ' active' : ''}`} onClick={toggleVideo} title={isVideoOff ? 'Activer caméra' : 'Couper caméra'}>
                {isVideoOff ? <VideoOff size={15}/> : <Video size={15}/>}
              </button>

              {/* FIX: masquer/afficher sa propre vignette */}
              <button
                className={`vroom-ctrl-btn${isLocalHidden ? ' screen-active' : ''}`}
                onClick={() => setLocalHidden(v => !v)}
                title={isLocalHidden ? 'Afficher ma vignette' : 'Masquer ma vignette'}
              >
                <EyeOff size={15}/>
              </button>

              <div style={{ width:1, height:20, background:C.border }}/>

              {/* Partage écran — hôte uniquement sur mobile pour garder de la place */}
              {(isHost || !isMobile) && (
                <button
                  className={`vroom-ctrl-btn${screenStream ? ' screen-active' : ''}`}
                  onClick={toggleScreenShare}
                  title={screenStream ? 'Arrêter le partage' : 'Partager l\'écran'}
                >
                  {screenStream ? <MonitorOff size={15}/> : <Monitor size={15}/>}
                </button>
              )}

              {/* Plein écran */}
              <button className="vroom-ctrl-btn" onClick={toggleFullscreen} title="Plein écran">
                <Maximize2 size={15}/>
              </button>

              {/* Mobile : toggle sidebar */}
              {isMobile && (
                <>
                  <div style={{ width:1, height:20, background:C.border }}/>
                  <button
                    className={`vroom-ctrl-btn${sidebarOpen ? ' screen-active' : ''}`}
                    onClick={() => setSidebarOpen(v => !v)}
                    title="Outils"
                  >
                    <Pencil size={15}/>
                  </button>
                </>
              )}

              {/* Indicateur screen share */}
              {screenStream && (
                <div style={{ display:'flex', alignItems:'center', gap:5, padding:'3px 8px', background:'rgba(34,211,238,0.08)', border:`1px solid ${C.borderCy}` }}>
                  <Dot color={C.cyan} pulse size={4}/>
                  <M size={8} color={C.cyan}>Partage_Actif</M>
                </div>
              )}
            </div>
          </div>

          {/* ── SIDEBAR OUTILS ───────────────────────────────────────────── */}
          <AnimatePresence>
            {(sidebarOpen || !isMobile) && (
              <motion.aside
                initial={isMobile ? { y:'100%' } : { x:0 }}
                animate={isMobile ? { y:0 } : { x:0 }}
                exit={isMobile ? { y:'100%' } : { x:0 }}
                transition={{ type:'spring', damping:28, stiffness:260 }}
                className={isMobile ? 'vroom-sidebar-drawer' : ''}
                style={isMobile ? {
                  position: 'fixed',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  zIndex: 2100,
                  background: 'rgba(2,6,23,0.98)',
                  borderTop: `1px solid ${C.border}`,
                  maxHeight: '65vh',
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                } : {
                  width: 300,
                  borderLeft: `1px solid ${C.border}`,
                  background: 'rgba(0,0,0,0.5)',
                  display: 'flex',
                  flexDirection: 'column',
                  flexShrink: 0,
                }}
              >
                {/* Tabs */}
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', borderBottom:`1px solid ${C.border}`, flexShrink:0 }}>
                  {TABS.map(({ id, icon:Icon, label }) => (
                    <button key={id} onClick={() => setActiveTab(id)}
                      style={{ display:'flex', flexDirection:'column', alignItems:'center', padding:'8px 4px', gap:3, border:'none', background:'none', cursor:'pointer', color:activeTab===id?accent:C.textFaint, borderBottom:activeTab===id?`1.5px solid ${accent}`:'1.5px solid transparent', transition:'all 0.15s' }}>
                      <Icon size={12}/><M size={8}>{label}</M>
                    </button>
                  ))}
                </div>

                {/* Contenu onglet — hauteur fixe desktop, auto mobile */}
                <div style={{ flex:1, overflow:'hidden', height: isMobile ? '55vh' : undefined }}>
                  {activeTab === 'whiteboard' && <Whiteboard/>}
                  {activeTab === 'notes'      && <SharedNotes/>}

                  {activeTab === 'chat' && (
                    <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>
                      <div style={{ flex:1, overflowY:'auto', padding:10, display:'flex', flexDirection:'column', gap:8 }}>
                        {messages.length === 0 && (
                          <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', opacity:0.15 }}>
                            <M size={9}>Canal_Inactif</M>
                          </div>
                        )}
                        {messages.map((m, i) => (
                          <div key={i} style={{ display:'flex', flexDirection:'column', alignItems:m.self?'flex-end':'flex-start' }}>
                            <M size={8} color={C.textFaint} style={{ marginBottom:2 }}>
                              {m.self ? userName : m.sender} · {m.time}
                            </M>
                            <div style={{ padding:'7px 10px', background:m.self?'rgba(34,211,238,0.08)':'rgba(255,255,255,0.04)', border:`1px solid ${m.self?'rgba(34,211,238,0.2)':C.border}`, maxWidth:'90%', fontSize:11, color:C.textSub, lineHeight:1.5 }}>
                              {m.text}
                            </div>
                          </div>
                        ))}
                        <div ref={msgEndRef}/>
                      </div>
                      <div style={{ padding:8, borderTop:`1px solid ${C.border}`, display:'flex', gap:5 }}>
                        <input
                          value={msgInput}
                          onChange={e => setMsgInput(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && sendMsg()}
                          placeholder="Message..."
                          style={{ flex:1, background:'rgba(255,255,255,0.04)', border:`1px solid ${C.border}`, padding:'7px 10px', color:C.text, fontSize:11, outline:'none', fontFamily:'inherit' }}
                        />
                        <button onClick={sendMsg}
                          style={{ padding:'7px 10px', background:C.cyan, border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                          <Send size={11} color="#000"/>
                        </button>
                      </div>
                    </div>
                  )}

                  {activeTab === 'participants' && (
                    <div style={{ padding:10, display:'flex', flexDirection:'column', gap:5, overflowY:'auto', maxHeight:'100%' }}>
                      {/* Soi */}
                      <div style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 10px', background:`${C.cyan}06`, border:`1px solid ${C.cyan}20` }}>
                        <div style={{ width:26, height:26, background:C.cyan, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, color:'#000' }}>
                          {userName[0]?.toUpperCase()}
                        </div>
                        <div>
                          <M size={10} color={C.text} w style={{ display:'block' }}>{userName}</M>
                          <M size={8} color={C.textFaint}>
                            {isHost ? '⚙ Hôte' : 'Participant'} · Connecté
                          </M>
                        </div>
                        <Dot color={C.emerald} pulse size={4} style={{ marginLeft:'auto' }}/>
                      </div>

                      {/* Remotes */}
                      {remoteArr.map(([pid, p]) => (
                        <div key={pid} style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 10px', border:`1px solid ${C.border}` }}>
                          <div style={{ width:26, height:26, background:'rgba(255,255,255,0.06)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, color:C.textFaint }}>
                            {p.name?.[0]?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <M size={10} color={C.text} w style={{ display:'block' }}>{p.name}</M>
                            <M size={8} color={C.textFaint}>
                              {p.role === 'host' ? '⚙ Hôte' : 'Participant'} · Connecté
                            </M>
                          </div>
                          <Dot color={C.emerald} size={4} style={{ marginLeft:'auto' }}/>
                        </div>
                      ))}

                      {remoteArr.length === 0 && (
                        <div style={{ padding:'18px', textAlign:'center', opacity:0.25 }}>
                          <M size={9} color={C.textFaint}>En attente...</M>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </motion.aside>
            )}
          </AnimatePresence>
        </main>

        {/* ── FOOTER ─────────────────────────────────────────────────────── */}
        <footer style={{
          height:28, flexShrink:0,
          background:'rgba(2,6,23,0.98)',
          borderTop:`1px solid ${C.border}`,
          display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'0 12px',
        }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <Dot color={C.emerald} pulse size={4}/>
            <M size={8} color='rgba(16,185,129,0.5)'>Stream_Active</M>
            {!isMobile && <M size={8} color={C.textFaint}>Room: {roomId}</M>}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {isHost && (
              <div style={{ padding:'2px 7px', background:`${C.amber}10`, border:`1px solid ${C.amber}25` }}>
                <M size={7} color={C.amber}>HOST_MODE</M>
              </div>
            )}
            <M size={8} color={`${C.cyan}25`}>Pesa_v1</M>
          </div>
        </footer>
      </motion.div>
    </VideoSessionContext.Provider>
  );
};

// ── CARD ATELIER ───────────────────────────────────────────────────────────
const AtelierCard = ({ atelier, unlocked, onSelect, handleVideoAction, isHost = false, delay = 0 }) => {
  const [hov, setHov] = useState(false);
  const accent = atelier.accentColor || C.cyan;
 
  // Tronquer proprement au dernier espace avant la limite
  const truncate = (str, max) => {
    if (!str || str.length <= max) return str;
    const cut = str.lastIndexOf(' ', max);
    return str.slice(0, cut > 60 ? cut : max) + '…';
  };
 
  const liveLabel = isHost ? 'LANCER' : 'REJOINDRE';
  const liveIcon  = isHost ? <Play size={10}/> : <Users size={10}/>;
  const liveColor = isHost ? C.amber : accent;
 
  return (
    <motion.div
      initial={{ opacity:0, y:10 }}
      animate={{ opacity:1, y:0 }}
      transition={{ delay }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        position: 'relative',
        overflow: 'hidden',
        background: hov && unlocked
          ? `linear-gradient(160deg, ${accent}07 0%, rgba(15,23,42,0.7) 60%)`
          : C.surface,
        border: `1px solid ${hov && unlocked ? `${accent}35` : C.border}`,
        borderRadius: 10,
        cursor: unlocked ? 'pointer' : 'not-allowed',
        transition: 'all 0.28s',
        transform: hov && unlocked ? 'translateY(-4px)' : 'none',
        boxShadow: hov && unlocked
          ? `0 16px 40px rgba(0,0,0,0.45), 0 0 0 1px ${accent}12, 0 0 24px ${accent}08`
          : '0 2px 8px rgba(0,0,0,0.2)',
        // Verrouillé : plus discret + légère désaturation
        opacity: unlocked ? 1 : 0.45,
        filter: unlocked ? 'none' : 'grayscale(0.3)',
      }}
    >
      {/* Ligne accent top — dégradé plus prononcé */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 2,
        background: `linear-gradient(90deg, ${unlocked ? accent : C.textFaint}70 0%, ${unlocked ? accent : C.textFaint}15 60%, transparent 100%)`,
        transition: 'opacity 0.3s',
      }}/>
 
      {/* Coins */}
      <Corners
        colorTL={unlocked ? accent : C.textFaint}
        colorBR={unlocked ? C.amber : C.textFaint}
        size={7}
      />
 
      {/* Scan lumineux au hover — ralenti à 5s, légèrement plus épais */}
      {hov && unlocked && (
        <motion.div
          animate={{ top: ['-2%', '104%'] }}
          transition={{ duration: 5, repeat: Infinity, ease: 'linear' }}
          style={{
            position: 'absolute', left: 0, right: 0,
            height: '1.5px', zIndex: 10,
            background: `linear-gradient(90deg, transparent, ${accent}80, transparent)`,
            boxShadow: `0 0 10px ${accent}60`,
            pointerEvents: 'none',
          }}
        />
      )}
 
      {/* Overlay gradient hover */}
      <div style={{
        position: 'absolute', inset: 0,
        background: `radial-gradient(ellipse at 30% 0%, ${accent}06 0%, transparent 65%)`,
        opacity: hov && unlocked ? 1 : 0,
        transition: 'opacity 0.4s',
        pointerEvents: 'none',
      }}/>
 
      <div style={{ padding: '18px 16px 16px', position: 'relative', zIndex: 1 }}>
 
        {/* ── Header : icône + tags + badge lock/sparkle ─────────────────── */}
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:14 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
 
            {/* Icône plus grande avec glow */}
            <div style={{
              width: 40, height: 40, flexShrink: 0,
              border: `1px solid ${accent}30`,
              background: `${accent}08`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              position: 'relative', borderRadius: 8,
              boxShadow: hov && unlocked ? `0 0 16px ${accent}25` : 'none',
              transition: 'box-shadow 0.3s',
            }}>
              {/* Halo glow derrière l'emoji */}
              <div style={{
                position: 'absolute', inset: -4, borderRadius: 12,
                background: `radial-gradient(circle, ${accent}18 0%, transparent 70%)`,
                opacity: hov ? 1 : 0.5,
                transition: 'opacity 0.3s',
              }}/>
              <span style={{
                fontSize: 20,
                filter: `drop-shadow(0 0 6px ${accent}60)`,
                position: 'relative', zIndex: 1,
              }}>
                {atelier.icon}
              </span>
            </div>
 
            {/* Tags — 2 max */}
            <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
              {atelier.tags?.slice(0,2).map(t => (
                <span key={t} style={{
                  padding: '2px 8px',
                  background: `${accent}10`,
                  border: `1px solid ${accent}22`,
                  borderRadius: 999,
                  fontFamily: "'Roboto Mono', monospace",
                  fontSize: 8, letterSpacing: '0.08em',
                  textTransform: 'uppercase', color: accent,
                }}>
                  {t}
                </span>
              ))}
            </div>
          </div>
 
          {/* Badge état */}
          {!unlocked
            ? <Lock size={13} color={C.textFaint} style={{ opacity:0.6, flexShrink:0 }}/>
            : <Sparkles size={13} color={accent} style={{ flexShrink:0, filter:`drop-shadow(0 0 4px ${accent}80)` }}/>
          }
        </div>
 
        {/* ── Titre + subtitle ────────────────────────────────────────────── */}
        <h3 style={{
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontSize: 15,
          fontWeight: 700,
          color: unlocked ? C.text : C.textSub,
          margin: '0 0 3px',
          letterSpacing: '-0.015em',
          lineHeight: 1.25,
        }}>
          {atelier.title}
        </h3>
 
        <p style={{
          fontFamily: "'Roboto Mono', monospace",
          fontSize: 9,
          color: `${accent}65`,
          margin: '0 0 10px',
          letterSpacing: '0.04em',
          lineHeight: 1.3,
        }}>
          {atelier.subtitle}
        </p>
 
        {/* ── Description — tronquée proprement à 120 chars ──────────────── */}
        <p style={{
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontSize: 12,
          color: unlocked ? C.textSub : C.textFaint,
          lineHeight: 1.65,
          margin: '0 0 14px',
          minHeight: 40, // hauteur minimale pour aligner les cards entre elles
        }}>
          {truncate(atelier.description || '', 120)}
        </p>
 
        {/* ── Pills méta — arrondies ──────────────────────────────────────── */}
        <div style={{ display:'flex', gap:6, marginBottom:14, flexWrap:'wrap' }}>
          {[
            { label: `${atelier.duration} min`,              color: accent    },
            { label: atelier.level,                          color: C.amber   },
            { label: `${atelier.exercises?.length ?? 0} ex`, color: C.emerald },
          ].map(({ label, color }) => (
            <div key={label} style={{
              padding: '3px 9px',
              background: `${color}09`,
              border: `1px solid ${color}22`,
              borderRadius: 999,
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              <div style={{ width: 4, height: 4, borderRadius: '50%', background: color, opacity: 0.7 }}/>
              <span style={{ fontFamily:"'Roboto Mono', monospace", fontSize:9, color }}>{label}</span>
            </div>
          ))}
        </div>
 
        {/* ── Actions ─────────────────────────────────────────────────────── */}
        <div style={{ paddingTop: 12, borderTop:`1px solid ${C.border}`, display:'flex', gap:7 }}>
 
          {/* CONSULTER */}
          <button
            onClick={e => { e.stopPropagation(); unlocked && onSelect(atelier); }}
            disabled={!unlocked}
            style={{
              flex: 1, padding: '7px 10px',
              background: 'none',
              border: `1px solid ${unlocked ? `${accent}28` : C.border}`,
              color: unlocked ? accent : C.textFaint,
              cursor: unlocked ? 'pointer' : 'default',
              fontFamily: "'Roboto Mono', monospace",
              fontSize: 9, fontWeight: 700,
              letterSpacing: '0.08em', textTransform: 'uppercase',
              borderRadius: 4,
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { if (unlocked) { e.currentTarget.style.background=`${accent}10`; e.currentTarget.style.borderColor=`${accent}45`; }}}
            onMouseLeave={e => { e.currentTarget.style.background='none'; e.currentTarget.style.borderColor=`${accent}28`; }}
          >
            {unlocked ? 'CONSULTER' : 'VERROUILLÉ'}
          </button>
 
          {/* LANCER / REJOINDRE */}
          {atelier.videoCallEnabled && unlocked && (
            <button
              onClick={e => { e.stopPropagation(); handleVideoAction(atelier); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '7px 13px',
                background: `${liveColor}14`,
                border: `1px solid ${liveColor}35`,
                color: liveColor,
                cursor: 'pointer',
                fontFamily: "'Roboto Mono', monospace",
                fontSize: 9, fontWeight: 700,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                borderRadius: 4,
                transition: 'all 0.15s',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => { e.currentTarget.style.background=`${liveColor}26`; e.currentTarget.style.boxShadow=`0 0 12px ${liveColor}20`; }}
              onMouseLeave={e => { e.currentTarget.style.background=`${liveColor}14`; e.currentTarget.style.boxShadow='none'; }}
            >
              {liveIcon} {liveLabel}
            </button>
          )}
        </div>
 
      </div>
    </motion.div>
  );
};

// ── PAGE PRINCIPALE ────────────────────────────────────────────────────────
const AteliersPage = () => {
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();
  const { isUnlocked, unlockedIds, unlockedCount } = useAtelierUnlock();
  const { user } = useAuth();

  const isHost = ['admin', 'superadmin'].includes(user?.role);

  const userName = user?.prenom
    ? `${user.prenom}${user.nom ? ' ' + user.nom : ''}`
    : user?.username || user?.email?.split('@')[0] || null;
    
  const [selectedAtelier, setSelectedAtelier] = useState(null);
  const [liveAtelier,     setLiveAtelier]     = useState(null);
  const [isMinimized,     setIsMinimized]     = useState(false);

  const [filter, setFilter]                   = useState('all');
  const isMobile = useIsMobile(640);
  const [noSessionAtelier, setNoSessionAtelier] = useState(null);
  const [atelierVideo, setatelierVideo] = useState(null);
  const [loadingVideo, setLoadingVideo] = useState(false);
  

  useEffect(() => {
    const slug = searchParams.get('atelier');
    if (!slug) return;
    const found = ATELIERS_LIBRARY.find(a => a.slug === slug);
    if (found) setSelectedAtelier(found);
    if (found && searchParams.get('video') === '1') setLiveAtelier(found);
  }, [searchParams]);  

  const handleLaunchVideo = useCallback((atelier) => {
    // Early return : on sort tôt si conditions non remplies
    if (!isUnlocked(atelier.id) || !isHost) return;

    setLiveAtelier(atelier);
    setIsMinimized(false);
  }, [
    isUnlocked,     // ← dépendance importante si c'est une fonction
    isHost,         // ← dépend si c'est un booléen ou état/calculé
    setLiveAtelier, // ← stable grâce à useState
    setIsMinimized  // ← stable grâce à useState
  ]); 
  
  const handleJoinVideo = useCallback((atelier) => {
    if (!isUnlocked(atelier.id)) return;
    const checkSocket = io(process.env.REACT_APP_SOCKET_URL + '/video', {
      withCredentials: true, transports: ['polling'], reconnection: false,
    });
    const roomId = `atelier-${atelier.slug}`;
    const timeout = setTimeout(() => {
      checkSocket.disconnect();
      setLiveAtelier(atelier); // dégradé : on laisse entrer
    }, 4000);

    checkSocket.on('connect', () => checkSocket.emit('room-check', { roomId }));
    checkSocket.on('room-status', ({ active }) => {
      clearTimeout(timeout);
      checkSocket.disconnect();
      if (active) { setLiveAtelier(atelier); setIsMinimized(false); }
      else { setNoSessionAtelier(atelier.id); setTimeout(() => setNoSessionAtelier(null), 4000); }
    });
    checkSocket.on('connect_error', () => { clearTimeout(timeout); checkSocket.disconnect(); setLiveAtelier(atelier); setIsMinimized(false); });
  }, [isUnlocked]);
 
  const handleVideoAction = useCallback((atelier) => {
    if (isHost) {
      handleLaunchVideo(atelier);
    } else {
      handleJoinVideo(atelier);
    }
  }, [isHost, handleLaunchVideo, handleJoinVideo]);

  const FILTERS = [
    { id:'all',        label:'Tous' },
    { id:'unlocked',   label:'Débloqués' },
    { id:'scolaire',   label:'Scolaire' },
    { id:'famille',    label:'Famille' },
    { id:'carrière',   label:'Carrière' },
    { id:'créativité', label:'Créativité' },
    { id:'éducation',  label:'Education' },
  ];

  const filtered = ATELIERS_LIBRARY.filter(a => {
    if (filter==='all')      return true;
    if (filter==='unlocked') return isUnlocked(a.id);
    return a.tags?.includes(filter);
  });

  return (
    <div style={{ display:'flex', flexDirection:'column', minHeight:'100vh', background:C.bg, color:C.text, fontFamily:"'Plus Jakarta Sans', sans-serif", overflow:'hidden' }}>
      <style>{GLOBAL_CSS}</style>
      <PerspectiveGrid/>
      <div style={{ position:'fixed', inset:0, zIndex:0, pointerEvents:'none', background:'radial-gradient(circle at 20% 20%, rgba(34,211,238,0.03) 0%, transparent 40%)' }}/>
      <div style={{ position:'fixed', inset:0, zIndex:0, pointerEvents:'none', background:'radial-gradient(circle at 80% 80%, rgba(245,158,11,0.04) 0%, transparent 40%)' }}/>

      {/* HEADER */}
      <header style={{ height:50, background:'rgba(2,6,23,0.96)', backdropFilter:'blur(24px)', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 16px', flexShrink:0, position:'relative', zIndex:10 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button
                      onClick={() => navigate('/dashboard')}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: C.textMute, padding: 0, transition: 'color 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.color = C.textDim}
                      onMouseLeave={e => e.currentTarget.style.color = C.textMute}
                    >
                      <ArrowLeft size={13} />
                      <Mono size={10} color="inherit">Dashboard</Mono>
                    </button>
          <span style={{ width:1, height:18, background:C.border }}/>
          <div style={{ display:'flex', alignItems:'center', gap:7 }}>
            <Sparkles size={12} color={C.amber}/>
            <M size={11} color={C.text} w>Ateliers</M>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          {unlockedIds>0&&(
            <div style={{ display:'flex', alignItems:'center', gap:5, padding:'3px 8px', border:`1px solid ${C.emerald}25`, background:`${C.emerald}06` }}>
              <Dot color={C.emerald} pulse size={4}/>
              <M size={9} color={C.emerald}>{unlockedIds} débloqué{unlockedIds>1?'s':''}</M>
            </div>
          )}
          {!isMobile && <M size={9} color={C.textFaint}>{ATELIERS_LIBRARY.length} total</M>}
        </div>
      </header>

      {/* CONTENU */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',         
        minHeight: 0,                    
        position: 'relative',
        zIndex: 1,
        overflow: 'visible',
        marginTop:8              
      }}>
        {selectedAtelier ? (
          <AtelierDetail
            atelier={selectedAtelier}
            isUnlocked={isUnlocked(selectedAtelier.id)}
            onBack={()=>setSelectedAtelier(null)}            
            handleVideoAction={handleVideoAction}
            isHost={isHost}
            atelierVideo={atelierVideo}
          />
        ) : (
          <div className="ateliers-content" style={{
              flex: 1,
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',  
              padding: '0 16px 80px 16px',             
            }}>

            {/* Hero header */}
            <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} style={{ marginBottom: isMobile ? 20 : 28 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                <Dot color={C.amber} pulse size={5}/>
                <M size={9} color={`${C.amber}60`}>Pesa_Ateliers · Bibliothèque_Active</M>
              </div>
              <h1 style={{ fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize: isMobile ? 26 : 'clamp(28px,4vw,44px)', fontWeight:800, color:C.text, letterSpacing:'-0.02em', margin:'0 0 8px', lineHeight:1.1 }}>
                Vos <span style={{ background:`linear-gradient(135deg, ${C.cyan}, ${C.amber})`, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>Ateliers</span>
              </h1>
              <p style={{ fontSize:13, color:C.textSub, lineHeight:1.65, maxWidth:480, margin:0 }}>
                Des espaces de pratique débloqués au fil de vos simulations — théorie, exercices et sessions live.
              </p>
            </motion.div>

            {/* Filters */}
            <div className="filters-row">
              {FILTERS.map(f=>(
                <button key={f.id} onClick={()=>setFilter(f.id)}
                  style={{ padding:'4px 12px', background:filter===f.id?`${C.cyan}12`:'none', border:`1px solid ${filter===f.id?`${C.cyan}35`:C.border}`, color:filter===f.id?C.cyan:C.textFaint, cursor:'pointer', fontFamily:"'Roboto Mono', monospace", fontSize:9, letterSpacing:'0.1em', textTransform:'uppercase', transition:'all 0.15s', display:'flex', alignItems:'center', gap:5 }}>
                  {f.label}
                  {f.id==='unlocked'&&unlockedIds>0&&(
                    <span style={{ width:13, height:13, display:'inline-flex', alignItems:'center', justifyContent:'center', background:C.emerald, color:'#000', fontSize:8, fontWeight:700, borderRadius:'50%' }}>{unlockedIds}</span>
                  )}
                </button>
              ))}
            </div>

            {/* Grid */}
            <div className="ateliers-grid">
              {filtered.map((atelier,i)=>(
                <AtelierCard
                  key={atelier.id}
                  atelier={atelier}
                  unlocked={isUnlocked(atelier.id)}
                  onSelect={setSelectedAtelier}
                  handleVideoAction={handleVideoAction}
                  isHost={isHost}
                  delay={i*0.04}
                  
                />
              ))}
            </div>

            {filtered.length===0&&(
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'50px 0', opacity:0.25 }}>
                <Lock size={36} color={C.textFaint} style={{ marginBottom:10 }}/>
                <M size={10} color={C.textFaint}>Aucun_atelier · Complétez_un_run_pour_débloquer</M>
              </div>
            )}

            {unlockedCount===0 && (
              <motion.div initial={{opacity:0}} animate={{opacity:1}} transition={{delay:0.5}}
                style={{ marginTop:28, padding:'16px 18px', border:`1px solid ${C.amber}20`, background:`${C.amber}05`, position:'relative' }}>
                <Corners colorTL={C.amber} colorBR={C.cyan}/>
                <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
                  <Sparkles size={16} color={C.amber} style={{ flexShrink:0, marginTop:2 }}/>
                  <div style={{ minWidth:0 }}>
                    <M size={11} color={C.amber} w style={{display:'block',marginBottom:5}}>Comment débloquer des ateliers ?</M>
                    <p style={{ fontSize:12, color:C.textSub, lineHeight:1.65, margin:'0 0 12px' }}>
                      Complétez un run dans le <strong style={{color:C.text}}>Centre d'Ajustement</strong>. Chaque simulation débloque un ou plusieurs ateliers correspondants à votre situation.
                    </p>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                      {[
                        {run:'Instruction / Orientation', atelier:'Prépa Post-Bac', icon:'🎓'},
                        {run:'Assistante Maternelle', atelier:'Parentalité & Éveil', icon:'🧡'},
                        {run:'Formation', atelier:'Identité & Personnalité', icon:'🎑'},
                      ].map(({run,atelier,icon})=>(
                        <div key={run} style={{ display:'flex', alignItems:'center', gap:7, padding:'5px 10px', background:'rgba(255,255,255,0.02)', border:`1px solid ${C.border}` }}>
                          <span style={{ fontSize:13 }}>{icon}</span>
                          <div>
                            <M size={8} color={C.textFaint} style={{display:'block'}}>{run}</M>
                            <M size={8} color={C.amber}>→ {atelier}</M>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <button onClick={()=>navigate('/centre-intro')} style={{ marginTop:14, display:'inline-flex', alignItems:'center', gap:7, padding:'7px 16px', background:`${C.amber}12`, border:`1px solid ${C.amber}30`, color:C.amber, cursor:'pointer', fontFamily:"'Roboto Mono', monospace", fontSize:9, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', transition:'all 0.15s' }}
                  onMouseEnter={e=>e.currentTarget.style.background=`${C.amber}20`} onMouseLeave={e=>e.currentTarget.style.background=`${C.amber}12`}>
                  <Play size={10}/> Démarrer_un_Run
                </button>
              </motion.div>
            )}

            <div style={{ height: 24 }} />
          </div>
        )}
      </div>

      {/* FOOTER */}
      <footer style={{ height:30, background:'rgba(2,6,23,0.97)', borderTop:`1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 16px', flexShrink:0, position:'relative', zIndex:10 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <Dot color={C.emerald} size={4}/>
          {!isMobile && <M size={8} color='rgba(255,255,255,0.15)'>Apprendre / Enseigner / Transformer</M>}
        </div>
        <M size={8} color={`${C.cyan}20`}>Pesa_Ateliers_v1</M>
      </footer>

       
      {/* ── VIDEOROOM PLEIN ÉCRAN — survit à la navigation ── */}
      <AnimatePresence>
        {noSessionAtelier && (
          <motion.div
            initial={{ opacity:0, y:10 }}
            animate={{ opacity:1, y:0 }}
            exit={{ opacity:0, y:10 }}
            style={{
              position:'fixed', bottom: isMobile ? 60 : 20, left:'50%',
              transform:'translateX(-50%)', zIndex:3000,
              padding:'10px 18px',
              background:'rgba(2,6,23,0.97)',
              border:'1px solid rgba(245,158,11,0.35)',
              display:'flex', alignItems:'center', gap:10,
              backdropFilter:'blur(12px)',
              boxShadow:'0 8px 32px rgba(0,0,0,0.5)',
              whiteSpace:'nowrap',
            }}
          >
            <span style={{ width:7, height:7, borderRadius:'50%', background:'#f59e0b', boxShadow:'0 0 8px #f59e0b', flexShrink:0 }}/>
            <span style={{ fontFamily:"'Roboto Mono', monospace", fontSize:10, color:'#f1f5f9', letterSpacing:'0.05em' }}>
              Aucune_session_active · L'hôte n'a pas encore lancé
            </span>
          </motion.div>
        )}
      </AnimatePresence>
 
      {/* ── PiP FLOTTANT — visible quand la VideoRoom est minimisée ── */}
      {liveAtelier && isMinimized && (
        <motion.div
          initial={{ opacity:0, y:20 }}
          animate={{ opacity:1, y:0 }}
          exit={{ opacity:0, y:20 }}
          onClick={() => setIsMinimized(false)}
          style={{
            position:'fixed',
            bottom: isMobile ? 50 : 16,
            right: 16,
            zIndex: 1500,
            display:'flex', alignItems:'center', gap:10,
            padding:'10px 14px',
            background:'rgba(2,6,23,0.97)',
            border:`1px solid rgba(34,211,238,0.3)`,
            cursor:'pointer',
            backdropFilter:'blur(12px)',
            boxShadow:'0 8px 32px rgba(0,0,0,0.5)',
            maxWidth: isMobile ? 'calc(100vw - 32px)' : 280,
          }}
        >
          <span style={{ position:'absolute', top:-1, left:-1, width:6, height:6, borderTop:'1.5px solid #22d3ee', borderLeft:'1.5px solid #22d3ee' }}/>
          <span style={{ position:'absolute', bottom:-1, right:-1, width:6, height:6, borderBottom:'1.5px solid #f59e0b', borderRight:'1.5px solid #f59e0b' }}/>
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <span style={{ width:7, height:7, borderRadius:'50%', background:'#10b981', boxShadow:'0 0 8px #10b981', animation:'pulse 2s ease-in-out infinite', flexShrink:0 }}/>
            <div style={{ minWidth:0 }}>
              <span style={{ fontFamily:"'Roboto Mono', monospace", fontSize:9, color:'#f1f5f9', fontWeight:700, letterSpacing:'0.05em', display:'block', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {liveAtelier.title}
              </span>
              <span style={{ fontFamily:"'Roboto Mono', monospace", fontSize:8, color:'rgba(34,211,238,0.6)', letterSpacing:'0.08em' }}>
                Session_Active · Cliquer pour revenir
              </span>
            </div>
          </div>
          <button
            onClick={e => { e.stopPropagation(); setLiveAtelier(null); setIsMinimized(false); }}
            style={{ display:'flex', alignItems:'center', justifyContent:'center', width:22, height:22, background:'rgba(251,113,133,0.1)', border:'1px solid rgba(251,113,133,0.2)', color:'#fb7185', cursor:'pointer', flexShrink:0, marginLeft:'auto' }}
          >
            <X size={10}/>
          </button>
        </motion.div>
      )}
 
      {/* ── VIDEOROOM PLEIN ÉCRAN ── */}
      <AnimatePresence>
        {liveAtelier && !isMinimized && (
          <AtelierVideoRoom
            atelier={liveAtelier}
            isHost={isHost}
            userName={userName}
            onClose={() => { setLiveAtelier(null); setIsMinimized(false); }}
            onMinimize={() => setIsMinimized(true)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AteliersPage;